// helper functions for schedule-based stats
import { startOfWeek, addWeeks, isEqual, isBefore, startOfMonth, startOfYear, addMonths, addYears } from 'date-fns';
import { ScheduleBasedStatsDto, ScheduleBasedTherapyRxFrequencyDto } from '../../../api-client';

interface GroupedData {
    [locationId: string]: ScheduleBasedStatsDto[]
}

interface TotalRxCountData {
    locationId: string, 
    locationName: string,
    date: Date, 
    totalCount: number
}

// function to calculate total number of Rxs by bin (week or day)
export const calculateScheduleBasedTotalRx = (scheduleData: ScheduleBasedStatsDto[], binType: 'day' | 'week' | 'month' | 'year'): TotalRxCountData[] => {
    // Array to store the results
    const totalCountData: TotalRxCountData[] = [];

    // Group by location
    const groupedData: GroupedData = Object.groupBy(scheduleData, ({ locationId }) => locationId) as GroupedData;

    Object.keys(groupedData).forEach(locationId => {
        // Sort by date, ascending order
        groupedData[locationId].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
        
        const firstEntryDate = new Date(groupedData[locationId][0].date);
        const lastEntryDate = new Date(groupedData[locationId][groupedData[locationId].length - 1].date);

        if (binType === 'day') {
            groupedData[locationId].forEach((entry) => {
                totalCountData.push({ 
                    locationId: entry.locationId, 
                    locationName: entry.locationName,
                    date: new Date(entry.date), 
                    totalCount: entry.rxCount, 
                });
            });
        } else {
            let binStart: Date;
            let nextBinStart: Date;

            if (binType === 'week') {
                binStart = startOfWeek(firstEntryDate, { weekStartsOn: 1 });
            } else if (binType === 'month') {
                binStart = startOfMonth(firstEntryDate);
            } else {
                binStart = startOfYear(firstEntryDate);
            }
            
            while(isBefore(binStart, lastEntryDate)) {
                if (binType === 'week') {
                    nextBinStart = addWeeks(binStart, 1);
                } else if (binType === 'month') {
                    nextBinStart = addMonths(binStart, 1);
                } else {
                    nextBinStart = addYears(binStart, 1);
                }
                
                // count total Rx count in current bin
                const total = groupedData[locationId].filter(entry => 
                    new Date(entry.date) >= binStart && new Date(entry.date) < nextBinStart
                ).reduce((total, entry) => total + entry.rxCount, 0);
            
                totalCountData.push({ 
                    locationId: locationId, 
                    locationName: groupedData[locationId][0].locationName,
                    date: binStart, 
                    totalCount: total 
                });

                binStart = nextBinStart;
            }
        }
    });
    return totalCountData;
}


interface TotalApptValueData {
    locationId: string, 
    locationName: string,
    date: Date, 
    totalValue: number
}

// function to calculate total appointment value by bin (week or day)
export const calculateScheduleBasedTotalApptValue = (scheduleData: ScheduleBasedStatsDto[], binType: 'day' | 'week' | 'month' | 'year'): TotalApptValueData[] => {
    // Array to store the results
    const totalValueData: TotalApptValueData[] = [];

    // Group by locationId 
    const groupedData: GroupedData = Object.groupBy(scheduleData, ({ locationId }) => locationId) as GroupedData;

    Object.keys(groupedData).forEach(locationId => {
        // Sort by date, ascending order
        groupedData[locationId].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const firstEntryDate = new Date(groupedData[locationId][0].date);
        const lastEntryDate = new Date(groupedData[locationId][groupedData[locationId].length - 1].date);

        if (binType === 'day') {
            groupedData[locationId].forEach((entry) => {            
                totalValueData.push({ 
                    locationId: entry.locationId, 
                    locationName: entry.locationName,
                    date: new Date(entry.date), 
                    totalValue: entry.value 
                });
            });
        } else {
            let binStart: Date;
            let nextBinStart: Date;

            if (binType === 'week') {
                binStart = startOfWeek(firstEntryDate, { weekStartsOn: 1 });
            } else if (binType === 'month') {
                binStart = startOfMonth(firstEntryDate);
            } else {
                binStart = startOfYear(firstEntryDate);
            }
            
            while(isBefore(binStart, lastEntryDate)) {
                if (binType === 'week') {
                    nextBinStart = addWeeks(binStart, 1);
                } else if (binType === 'month') {
                    nextBinStart = addMonths(binStart, 1);
                } else {
                    nextBinStart = addYears(binStart, 1);
                }
            
                // calculate total value in current bin
                const total = groupedData[locationId].filter(entry => 
                    new Date(entry.date) >= binStart && new Date(entry.date) < nextBinStart
                ).reduce((total, entry) => total + entry.value, 0);

                totalValueData.push({
                    locationId: locationId,
                    locationName: groupedData[locationId][0].locationName,
                    date: binStart,
                    totalValue: total,
                });

                binStart = nextBinStart;
            }
        }
    });
    return totalValueData;
}


interface TotalGapsData {
    locationId: string, 
    locationName: string,
    date: Date, 
    totalGaps: number
}

// function to calculate total number of gaps by bin (week or day)
export const calculateScheduleBasedTotalGaps = (scheduleData: ScheduleBasedStatsDto[], binType: 'day' | 'week' | 'month' | 'year'): TotalGapsData[] => {
    // Array to store the results
    const totalGapsData: TotalGapsData[] = [];

    // console.log("sched data length: ", scheduleData.length);
    // console.log("unique dates: ", [...new Set(scheduleData.map((entry) => entry.date))].length);

    // Group by locationId 
    const groupedData: GroupedData = Object.groupBy(scheduleData, ({ locationId }) => locationId) as GroupedData;

    Object.keys(groupedData).forEach(locationId => {
        // Sort by date, ascending order
        groupedData[locationId].sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

        const firstEntryDate = new Date(groupedData[locationId][0].date);
        const lastEntryDate = new Date(groupedData[locationId][groupedData[locationId].length - 1].date);
        // console.log("sched data:", groupedData[locationId]);

        if (binType === 'day') {
            groupedData[locationId].forEach((entry) => {
                totalGapsData.push({ 
                    locationId: entry.locationId, 
                    locationName: entry.locationName,
                    date: new Date(entry.date), 
                    totalGaps: entry.gaps
                });
            });
        } else {
            let binStart: Date;
            let nextBinStart: Date;

            if (binType === 'week') {
                binStart = startOfWeek(firstEntryDate, { weekStartsOn: 1 });
            } else if (binType === 'month') {
                binStart = startOfMonth(firstEntryDate);
            } else {
                binStart = startOfYear(firstEntryDate);
            }
            
            while(isBefore(binStart, lastEntryDate)) {
                if (binType === 'week') {
                    nextBinStart = addWeeks(binStart, 1);
                } else if (binType === 'month') {
                    nextBinStart = addMonths(binStart, 1);
                } else {
                    nextBinStart = addYears(binStart, 1);
                }
            
                // calculate total gaps in current bin
                const total = groupedData[locationId].filter(entry => 
                    new Date(entry.date) >= binStart && new Date(entry.date) < nextBinStart
                ).reduce((total, entry) => total + entry.gaps, 0);

                totalGapsData.push({
                    locationId: locationId,
                    locationName: groupedData[locationId][0].locationName,
                    date: binStart,
                    totalGaps: total,
                });

                binStart = nextBinStart;
            }
        }
    });
    return totalGapsData;
}


interface TherapyRxFrequencyData {
    locationId: string,
    frequency: number,
    count: number,
    percentage: number
}

// function to count frequency of occurence
export const calculateTherapyRxFrequency = (therapyRxFrequencyData: ScheduleBasedTherapyRxFrequencyDto[]): TherapyRxFrequencyData[] => {
    const frequencyData: TherapyRxFrequencyData[] = [];

    // Group by locationId 
    const groupedData = Object.groupBy(therapyRxFrequencyData, ({ locationId }) => locationId);

    Object.keys(groupedData).forEach(locationId => {
        const countMap = new Map<number, number>();

        groupedData[locationId]?.forEach((entry) => {
            const frequency = entry.therapyRxFrequency;
            countMap.set(frequency, (countMap.get(frequency) || 0) + 1);
        });   

        // Calculate total for this location
        const total = Array.from(countMap.values()).reduce((sum, val) => sum + val, 0);

        // sort by frequency ascending
        const sortedFrequency = Array.from(countMap.entries()).sort((a, b) => a[0] - b[0]);

        for (const [frequency, count] of sortedFrequency) {
            frequencyData.push({
                locationId: locationId, 
                frequency: frequency, 
                count: count,
                percentage: total > 0 ? (count / total) : 0,
            });
        }
    });
    return frequencyData;
}


interface ContingencyTable {
    rowName: string[],
    colName: string[],
    table: Record<string, Record<string, number>>,
}

// function to create a contingency table for the therapyRx frequencies
export const generateContingencyTable = (frequencyData: TherapyRxFrequencyData[]): ContingencyTable => {
    const table: Record<string, Record<string, number>> = {};
    const rowNames = new Set<string>();
    const colNames = new Set<string>();

    frequencyData.forEach(({ locationId, frequency, count }) => {
        rowNames.add(locationId);
        colNames.add(String(frequency));

        if (!table[locationId]) {
            table[locationId] = {};
        }
        table[locationId][frequency] = count;
    });

    return {
        rowName: Array.from(rowNames),
        colName: Array.from(colNames).sort(),
        table,
    };
}